import { ExtendProductType, ReceivedTransportItem } from 'skladon-types';

export const TRANSPORT_ITEM_DESCRIPTION_MAX_LENGTH = 512;

type Numeric = number | string | null | undefined;

export interface CustomsOrderProductInput {
    LINE_STATE?: string | null;
    COUNT: Numeric;
    EXTEND_PRODUCT_TYPE?: string | null;
    PRODUCT_PRICE?: Numeric;
    ORDER_CURRENCY_PRICE?: Numeric;
    PRODUCT?: {
        SKU?: string | null;
        NAME?: string | null;
        WEIGHT?: Numeric;
        HS_CODE?: string | null;
        ORIGIN_COUNTRY?: string | null;
        DESCRIPTION?: string | null;
    } | null;
}

const EXCLUDED_EXTEND_TYPES: readonly ExtendProductType[] = ['everyOrder', 'specificProduct'];

function toNumber(value: Numeric): number | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function cleanString(value: string | null | undefined, maxLength?: number): string | null {
    if (value === null || value === undefined) {
        return null;
    }
    const trimmed = String(value).trim();
    if (trimmed === '') {
        return null;
    }
    return maxLength === undefined ? trimmed : trimmed.slice(0, maxLength);
}

/**
 * Maps active order lines to customs line items (transport_items). Free-of-charge extension lines
 * (everyOrder / specificProduct) are skipped; set lines use the order currency price when present, otherwise the product price.
 */
export function buildCustomsLineItems(
    orderProducts: readonly CustomsOrderProductInput[],
    options: { currency: string | null }
): ReceivedTransportItem[] {
    const currency = cleanString(options.currency);
    return orderProducts
        .filter(
            (line) =>
                line.LINE_STATE === 'E' &&
                !(line.EXTEND_PRODUCT_TYPE && EXCLUDED_EXTEND_TYPES.includes(line.EXTEND_PRODUCT_TYPE as ExtendProductType))
        )
        .map((line) => {
            const price =
                line.EXTEND_PRODUCT_TYPE === 'set' && toNumber(line.ORDER_CURRENCY_PRICE) !== null
                    ? line.ORDER_CURRENCY_PRICE
                    : line.PRODUCT_PRICE;
            return {
                SKU: cleanString(line.PRODUCT?.SKU),
                NAME: cleanString(line.PRODUCT?.NAME),
                DESCRIPTION: cleanString(line.PRODUCT?.DESCRIPTION, TRANSPORT_ITEM_DESCRIPTION_MAX_LENGTH),
                COUNT: toNumber(line.COUNT) ?? 0,
                UNIT_PRICE: toNumber(price),
                CURRENCY: currency,
                WEIGHT: toNumber(line.PRODUCT?.WEIGHT),
                HS_CODE: cleanString(line.PRODUCT?.HS_CODE),
                ORIGIN_COUNTRY: cleanString(line.PRODUCT?.ORIGIN_COUNTRY)
            };
        });
}
